import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { QueryClientProvider } from '@tanstack/react-query'
import { dashboardQueryClient } from 'src/v3/manage/dashboard/query-client'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import FormControl from '@mui/material/FormControl'
import IconButton from '@mui/material/IconButton'
import InputLabel from '@mui/material/InputLabel'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import ClearIcon from '@mui/icons-material/Clear'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import PersonAddIcon from '@mui/icons-material/PersonAdd'
import { DataGridPremium } from '@mui/x-data-grid-premium'
import type { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-premium'
import DataGridToolbar from 'src/v3/components/data-grid-toolbar'
import { dataGridSx } from 'src/v3/manage/styles'
import { useNotifications } from 'src/v3/manage/hooks/use-notifications'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  attendeeStatusChanged,
  attendeeStatusPresetCleared,
  fetchManageEventAttendees,
  fetchManageEventCapacity,
  selectAttendeeStatusPreset,
  selectManageEventAttendees,
  selectManageEventAttendeesStatus,
  selectManageEventAttendeesTotal,
  selectManageEventById,
  selectManageEventCapacity,
} from '../manage-events.slice'
import InviteUsersDialog from '../components/invite-users-dialog'
import SessionScopePicker from '../components/session-scope-picker'
import { updateEventInviteStatus } from '../utils/api'
import { LICENSE_TYPE_COLORS } from '../utils/constants'
import { STATUS_LABELS, useStatusChipColor } from '../utils/invite-status'
import RsvpStatCards from '../components/rsvp-stat-cards'
import { useSessionScope } from '../hooks/use-session-scope'
import { useDebouncedValue } from '../hooks/use-debounced-value'
import type { EventAttendeeRow, EventInviteStatus } from '../types'

dayjs.extend(utc)

type Props = { eventID: string }

let STATUS_OPTIONS: { value: EventInviteStatus; label: string }[] = [
  { value: 'invited', label: 'Pending RSVP' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'waitlisted', label: 'Waitlisted' },
  { value: 'declined', label: 'Declined' },
  { value: 'attended', label: 'Attended' },
]

let LICENSE_OPTIONS = [
  { value: 'EMT', label: 'EMT' },
  { value: 'AEMT', label: 'AEMT' },
  { value: 'Paramedic', label: 'Paramedic' },
  { value: 'Registered Nurse', label: 'RN' },
  { value: 'Other', label: 'Other' },
]

// Managers can move an invite to any other status; capacity and the RSVP
// transition rules don't apply to them (the API audits every change).
let MANAGER_STATUSES: EventInviteStatus[] = [
  'invited',
  'accepted',
  'waitlisted',
  'declined',
  'attended',
]

let getRowHeight = () => 'auto' as const

// ─── RSVP actions menu cell ───────────────────────────────────────────────────

type ActionCellProps = {
  row: EventAttendeeRow
  onStatusChange: (rowId: string, newStatus: EventInviteStatus) => void
}

let ActionCell = ({ row, onStatusChange }: ActionCellProps) => {
  let [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  let isTerminal = !row.inviteID
  let transitions = row.status
    ? MANAGER_STATUSES.filter((s) => s !== row.status)
    : []

  return (
    <>
      <IconButton
        size="small"
        disabled={isTerminal}
        onClick={(e) => {
          e.stopPropagation()
          setAnchorEl(e.currentTarget)
        }}
        aria-label="Change RSVP status"
      >
        <MoreVertIcon sx={{ fontSize: 18 }} />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        slotProps={{
          paper: {
            sx: {
              background: '#26292d',
              border: '1px solid rgba(255,255,255,.14)',
              borderRadius: '6px',
              minWidth: 196,
            },
          },
        }}
      >
        {transitions.length === 0 ? (
          <MenuItem disabled>
            <Typography sx={{ fontSize: 13, opacity: 0.4 }}>
              No status changes available
            </Typography>
          </MenuItem>
        ) : (
          transitions.map((s) => (
            <MenuItem
              key={s}
              onClick={() => {
                setAnchorEl(null)
                onStatusChange(row.id, s)
              }}
              sx={{ fontSize: 13 }}
            >
              {STATUS_LABELS[s]}
            </MenuItem>
          ))
        )}
      </Menu>
    </>
  )
}

// ─── Attendees Tab ────────────────────────────────────────────────────────────

let AttendeesTab = ({ eventID }: Props) => {
  let dispatch = useAppDispatch()
  let rows = useAppSelector(selectManageEventAttendees) ?? []
  let status = useAppSelector(selectManageEventAttendeesStatus)
  let total = useAppSelector(selectManageEventAttendeesTotal)
  let capacity = useAppSelector(selectManageEventCapacity)
  let statusPreset = useAppSelector(selectAttendeeStatusPreset)
  let event = useAppSelector(selectManageEventById(eventID))

  let scopeState = useSessionScope()
  let { scope, range, singleSession } = scopeState
  let [inviteOpen, setInviteOpen] = useState(false)

  let [search, setSearch] = useState('')
  let [statusFilter, setStatusFilter] = useState(statusPreset)
  let [licenseFilter, setLicenseFilter] = useState('')
  let [page, setPage] = useState(0)
  let [pageSize, setPageSize] = useState(10)
  let debouncedSearch = useDebouncedValue(search)
  let rowsRef = useRef(rows)
  // eslint-disable-next-line fp/no-mutation
  rowsRef.current = rows
  let getStatusColor = useStatusChipColor()
  let { showError, showSuccess, NotificationComponent } = useNotifications()

  let pageSizeRef = useRef(pageSize)
  // eslint-disable-next-line fp/no-mutation
  pageSizeRef.current = pageSize

  // Consume the preset once on mount and clear it. Safe because goToAttendees
  // dispatches attendeeStatusPresetSet synchronously before activeEventTabSet(2),
  // so the store is already updated by the time this tab mounts and statusPreset
  // is captured correctly by useState above.
  useEffect(() => {
    if (statusPreset) dispatch(attendeeStatusPresetCleared())
  }, [dispatch])

  useEffect(() => {
    if (scope === 'single' && !singleSession) return
    dispatch(
      fetchManageEventCapacity({
        eventID,
        scope,
        range,
        sessionID: singleSession?.id,
      }),
    )
  }, [scope, range, singleSession, eventID, dispatch])

  // Single unified attendees fetch: fires on scope/range/session/filter/eventID changes.
  // Always resets to page 1 so we never send a stale page offset to the API.
  useEffect(() => {
    if (scope === 'single' && !singleSession) return
    setPage(0)
    dispatch(
      fetchManageEventAttendees({
        eventID,
        page: 1,
        pageSize: pageSizeRef.current,
        scope,
        range,
        sessionID: singleSession?.id,
        q: debouncedSearch || undefined,
        status: statusFilter || undefined,
        license: licenseFilter || undefined,
      }),
    )
  }, [
    scope,
    range,
    singleSession,
    eventID,
    debouncedSearch,
    statusFilter,
    licenseFilter,
    dispatch,
  ])

  let rsvpCounts = {
    pending: capacity?.totalPending ?? 0,
    accepted: capacity?.totalAccepted ?? 0,
    waitlisted: capacity?.totalWaitlisted ?? 0,
    declined: capacity?.totalDeclined ?? 0,
    attended: capacity?.totalAttended ?? 0,
  }

  let hasActiveFilters = !!search || !!statusFilter || !!licenseFilter

  let handleSearchChange = (val: string) => {
    setSearch(val)
    setPage(0)
  }

  let clearFilters = () => {
    setSearch('')
    setStatusFilter('')
    setLicenseFilter('')
  }

  // The view the manager is looking at right now. A status-change refetch reads
  // it when the PUT resolves, not when the change started, so a filter or page
  // change in between isn't overwritten by the old query.
  let currentQuery = {
    eventID,
    page: page + 1,
    pageSize,
    scope,
    range,
    sessionID: singleSession?.id,
    q: debouncedSearch || undefined,
    status: statusFilter || undefined,
    license: licenseFilter || undefined,
  }
  let currentQueryRef = useRef(currentQuery)
  // eslint-disable-next-line fp/no-mutation
  currentQueryRef.current = currentQuery

  let refetchCurrentView = useCallback(() => {
    let query = currentQueryRef.current
    dispatch(fetchManageEventAttendees(query))
    dispatch(
      fetchManageEventCapacity({
        eventID: query.eventID,
        scope: query.scope,
        range: query.range,
        sessionID: query.sessionID,
      }),
    )
  }, [dispatch])

  let linkedServiceID = event?.linkedServiceID
  let canInvite =
    scope === 'single' &&
    singleSession != null &&
    dayjs.utc(singleSession.ts).isAfter(dayjs.utc()) &&
    !!linkedServiceID

  let handleStatusChange = useCallback(
    (rowId: string, newStatus: EventInviteStatus) => {
      let row = rowsRef.current.find((r) => r.id === rowId)
      if (!row?.inviteID) return

      let previousStatus = row.status
      if (!previousStatus) return

      dispatch(attendeeStatusChanged({ id: rowId, status: newStatus }))

      updateEventInviteStatus(row.inviteID, newStatus)
        .then((result) => {
          // An override past a full limit is applied; the API says so.
          showSuccess(
            result?.capacityWarning
              ? `RSVP status updated. ${result.capacityWarning}`
              : 'RSVP status updated',
          )
          // A decline can promote waitlisted attendees server-side, so other rows
          // and the counts may have changed too.
          refetchCurrentView()
        })
        .catch((error) => {
          dispatch(attendeeStatusChanged({ id: rowId, status: previousStatus }))
          // 409 carries a server message worth showing, e.g. "CE has already
          // been issued for this session, so attendance can't be removed."
          showError(
            error?.response?.status === 409 && error.response.data
              ? error.response.data
              : 'Failed to update RSVP status',
          )
        })
    },
    [dispatch, showSuccess, showError, refetchCurrentView],
  )

  let columns: GridColDef<EventAttendeeRow>[] = useMemo(
    () => [
      {
        field: 'attendee',
        headerName: 'Attendee',
        flex: 2,
        minWidth: 200,
        sortable: true,
        valueGetter: (_val, row) =>
          `${row.lastName} ${row.firstName}`.trim() || row.primaryEmail,
        renderCell: (params: GridRenderCellParams<EventAttendeeRow>) => {
          let { row } = params
          let name =
            row.firstName || row.lastName
              ? `${row.firstName} ${row.lastName}`.trim()
              : null
          return (
            <Box className="flex flex-col justify-center py-3">
              {name ? (
                <>
                  <Typography
                    sx={{ fontWeight: 500, fontSize: 14, lineHeight: 1.3 }}
                  >
                    {name}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 12,
                      color: 'rgba(255,255,255,.5)',
                      lineHeight: 1.3,
                    }}
                  >
                    {row.primaryEmail}
                  </Typography>
                </>
              ) : (
                <Typography sx={{ fontSize: 14 }}>
                  {row.primaryEmail || row.userID}
                </Typography>
              )}
            </Box>
          )
        },
      },
      {
        field: 'licenseType',
        headerName: 'License',
        flex: 1,
        minWidth: 120,
        sortable: true,
        renderCell: (params: GridRenderCellParams<EventAttendeeRow>) => {
          let { value } = params
          if (!value)
            return (
              <Typography sx={{ color: 'rgba(255,255,255,.3)' }}>—</Typography>
            )
          let licColor = LICENSE_TYPE_COLORS[value] ?? '#72CCFF'
          return (
            <Chip
              label={value}
              size="medium"
              sx={{
                bgcolor: `color-mix(in srgb, ${licColor} 16%, transparent)`,
                color: licColor,
                border: `1px solid color-mix(in srgb, ${licColor} 40%, transparent)`,
              }}
            />
          )
        },
      },
      {
        field: 'status',
        headerName: 'RSVP Status',
        flex: 1.2,
        minWidth: 140,
        sortable: true,
        renderCell: (params: GridRenderCellParams<EventAttendeeRow>) => {
          let { value } = params
          if (!value)
            return (
              <Typography sx={{ color: 'rgba(255,255,255,.3)' }}>—</Typography>
            )
          let color = getStatusColor(value as EventInviteStatus)
          return (
            <Chip
              label={STATUS_LABELS[value as EventInviteStatus] ?? value}
              size="medium"
              sx={{
                bgcolor: `color-mix(in srgb, ${color} 16%, transparent)`,
                color,
                border: `1px solid color-mix(in srgb, ${color} 40%, transparent)`,
              }}
            />
          )
        },
      },
      {
        field: 'actions',
        headerName: 'Actions',
        flex: 0.6,
        minWidth: 80,
        sortable: false,
        filterable: false,
        disableExport: true,
        renderCell: (params: GridRenderCellParams<EventAttendeeRow>) => (
          <ActionCell row={params.row} onStatusChange={handleStatusChange} />
        ),
      },
    ],
    [handleStatusChange, getStatusColor],
  )

  let isLoading = status === 'uninitialized' || status === 'pending'

  return (
    <Box className="flex flex-col gap-4">
      <RsvpStatCards counts={rsvpCounts} />

      {/* Scope picker */}
      <SessionScopePicker
        eventID={eventID}
        {...scopeState}
        action={
          canInvite ? (
            <Button
              variant="outlined"
              startIcon={<PersonAddIcon />}
              onClick={() => setInviteOpen(true)}
            >
              Invite Users
            </Button>
          ) : null
        }
      />

      {/* Filter bar */}
      <Stack direction="row" className="flex-wrap items-center gap-4">
        <TextField
          label="Search attendees…"
          value={search}
          onChange={(e) => handleSearchChange(e.target.value)}
          className="flex-1"
          sx={{ minWidth: 170, backgroundColor: 'background.paperDark' }}
        />
        <FormControl
          className="flex-1"
          sx={{ minWidth: 170, backgroundColor: 'background.paperDark' }}
        >
          <InputLabel id="attendees-status-label" shrink>
            RSVP Status
          </InputLabel>
          <Select
            labelId="attendees-status-label"
            label="RSVP Status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            displayEmpty
          >
            <MenuItem value="">
              <em>All statuses</em>
            </MenuItem>
            {STATUS_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl
          className="flex-1"
          sx={{ minWidth: 170, backgroundColor: 'background.paperDark' }}
        >
          <InputLabel id="attendees-license-label" shrink>
            License
          </InputLabel>
          <Select
            labelId="attendees-license-label"
            label="License"
            value={licenseFilter}
            onChange={(e) => setLicenseFilter(e.target.value)}
            displayEmpty
          >
            <MenuItem value="">
              <em>All licenses</em>
            </MenuItem>
            {LICENSE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {hasActiveFilters && (
          <Button
            size="small"
            variant="text"
            color="secondary"
            startIcon={<ClearIcon />}
            onClick={clearFilters}
            className="shrink-0 whitespace-nowrap"
          >
            Clear filters
          </Button>
        )}
      </Stack>

      {/* Data grid */}
      <DataGridPremium
        rows={rows}
        columns={columns}
        loading={isLoading}
        rowCount={total}
        paginationMode="server"
        paginationModel={{ page, pageSize }}
        onPaginationModelChange={(model) => {
          setPage(model.page)
          setPageSize(model.pageSize)
          dispatch(
            fetchManageEventAttendees({
              eventID,
              page: model.page + 1,
              pageSize: model.pageSize,
              scope,
              range,
              sessionID: singleSession?.id,
              q: debouncedSearch || undefined,
              status: statusFilter || undefined,
              license: licenseFilter || undefined,
            }),
          )
        }}
        pagination
        pageSizeOptions={[10, 25, 50, 100]}
        slots={{
          toolbar: () => (
            <DataGridToolbar
              fileName="attendees"
              showQuickFilter={false}
              showColumnsButton={false}
              showFilterButton={false}
              showExport
            />
          ),
        }}
        showToolbar
        disableRowSelectionOnClick
        getRowHeight={getRowHeight}
        sx={{
          ...dataGridSx,
          '& .MuiDataGrid-row': { cursor: 'default' },
          '& .MuiDataGrid-cell': { alignItems: 'center', display: 'flex' },
        }}
      />
      {/* Not gated on canInvite: its start-time check would unmount an open
          dialog once the session starts. The server's 409 covers that case. */}
      {singleSession && linkedServiceID && (
        <InviteUsersDialog
          open={inviteOpen}
          onClose={() => setInviteOpen(false)}
          linkedServiceID={linkedServiceID}
          session={singleSession}
          onSaved={refetchCurrentView}
        />
      )}
      {NotificationComponent}
    </Box>
  )
}

let AttendeesTabWithQueryClient = ({ eventID }: Props) => (
  <QueryClientProvider client={dashboardQueryClient}>
    <AttendeesTab eventID={eventID} />
  </QueryClientProvider>
)

export default AttendeesTabWithQueryClient
